import React from 'react';
import { Group } from '@mantine/core';
import PropTypes from 'prop-types';
import IconBadge from '../iconBadge/IconBadge';
import { shotChannels } from 'utils/constants';

export const getShotChannel = (value) => {
    if (value == null || value === '') return null;
    return shotChannels.find((channel) => channel.value === value) ?? null;
};

const normalizeChannels = (input) => {
    if (input == null || input === '') return [];
    if (Array.isArray(input)) return input.filter((val) => val != null && val !== '');
    if (typeof input === 'string') {
        return input
            .split(',')
            .map((val) => val.trim())
            .filter(Boolean);
    }
    return [];
};

const renderChannelBadge = (val, idx, { hideUnknown, useLabel, sx, iconBadgeProps }) => {
    const channel = getShotChannel(val);

    if (!channel && hideUnknown) return null;

    const label = useLabel ? channel?.label ?? val : channel?.value ?? val;

    return (
        <IconBadge
            key={`${val}-${idx}`}
            color={channel?.color}
            icon={channel?.icon}
            sx={{ maxWidth: 150, ...sx }}
            {...iconBadgeProps}>
            {label}
        </IconBadge>
    );
};

/**
 * Displays one or more shot channels using IconBadge with configured color and icon.
 * Pass `channels` (or `values`) for an array, or `value` for a single channel.
 */
const ChannelBadge = ({
    value,
    values,
    channels,
    hideUnknown = true,
    useLabel = false,
    spacing = 3,
    sx = {},
    groupProps = {},
    ...iconBadgeProps
}) => {
    const channelValues = (() => {
        const fromChannels = normalizeChannels(channels);
        if (fromChannels.length) return fromChannels;
        const fromValues = normalizeChannels(values);
        if (fromValues.length) return fromValues;
        return value != null && value !== '' ? [value] : [];
    })();
    const badges = channelValues
        .map((val, idx) => renderChannelBadge(val, idx, { hideUnknown, useLabel, sx, iconBadgeProps }))
        .filter(Boolean);

    if (!badges.length) return null;

    if (badges.length === 1 && channels == null && values == null) {
        return badges[0];
    }

    return (
        <Group spacing={spacing} noWrap {...groupProps}>
            {badges}
        </Group>
    );
};

ChannelBadge.propTypes = {
    value: PropTypes.string,
    values: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    channels: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    hideUnknown: PropTypes.bool,
    useLabel: PropTypes.bool,
    spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sx: PropTypes.object,
    groupProps: PropTypes.object,
};

export default ChannelBadge;
